import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('authToken');
    if (token) {
      // Parse user data from the JWT token
      const userData = parseJwt(token);
      if (userData) {
        setUser({
          id: userData.id,
          name: userData.name || 'User',
          email: userData.email,
          role: userData.role
        });
      } else {
        // If token is invalid, clear it
        localStorage.removeItem('authToken');
      }
    }
    setLoading(false);
  }, []);

  // Helper function to parse JWT token
  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      if (response.data && response.data.success) {
        const { user, token } = response.data.data;
        setUser(user);
        localStorage.setItem('authToken', token);
        return { success: true, user };
      } else {
        return { success: false, error: response.data?.message || 'Login failed' };
      }
    } catch (err) {
      return { success: false, error: err.message || 'Invalid credentials' };
    }
  };

  const logout = async () => {
    try {
      // Call the logout API endpoint
      await authAPI.logout();
    } catch (err) {
      console.error('Error during logout:', err);
    } finally {
      // Clear user data and token regardless of API success
      setUser(null);
      localStorage.removeItem('authToken');
      navigate('/login');
    }
  };

  const refreshToken = async () => {
    try {
      const response = await authAPI.refreshToken();
      if (response.data && response.data.success) {
        const { token } = response.data.data;
        localStorage.setItem('authToken', token);
        // Update user data from new token
        const userData = parseJwt(token);
        if (userData) {
          setUser({
            id: userData.id,
            name: userData.name || 'User',
            email: userData.email,
            role: userData.role
          });
        }
        return token;
      }
    } catch (err) {
      console.error('Error refreshing token:', err);
      // If refresh fails, log the user out
      logout();
    }
  };

  const getToken = () => {
    return localStorage.getItem('authToken');
  };

  const value = {
    user,
    login,
    logout,
    refreshToken,
    loading,
    getToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};